/**
 * Live module step 3 — Übersicht matrix (PRD §4.4): per-month h / R&D columns,
 * year totals with %, clickable month headers + Berlin current-month highlight.
 * Numbers are client-side reduces over the existing live overview payload.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, Lock, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { YearSelect } from "@/components/ui/year-select";
import { formatPersonName } from "@/lib/format";
import { rdSharePct, round2 } from "@/shared/aggregation";
import { berlinToday } from "@/shared/dates";
import type { LiveEmployeeMonth, LiveOverview, MonthLockSummary } from "@/shared/schemas/tracking";

const OVERVIEW_COL_COUNT = 1 + 24 + 3 + 1; // employee + 12×(h,R&D) + year(h,R&D,%) + action

/** Opaque header pin — `bg-muted` (not /40) so month cells never show through. */
const STICKY_EMPLOYEE_HEADER =
  "sticky left-0 z-20 bg-muted px-3 py-3 align-bottom font-medium shadow-[2px_0_8px_-2px_rgba(0,0,0,0.12)]";
const STICKY_ACTION_HEADER =
  "sticky right-0 z-30 bg-muted px-3 py-3 align-bottom font-medium whitespace-nowrap shadow-[-2px_0_8px_-2px_rgba(0,0,0,0.12)]";
/** Opaque body pin — `bg-card` matches the scroll wrapper. Last column wins if pins overlap. */
const STICKY_EMPLOYEE_CELL =
  "sticky left-0 z-20 bg-card px-3 py-3 shadow-[2px_0_8px_-2px_rgba(0,0,0,0.12)]";
const STICKY_ACTION_CELL =
  "sticky right-0 z-30 whitespace-nowrap bg-card px-3 py-3 shadow-[-2px_0_8px_-2px_rgba(0,0,0,0.12)]";

/** True when any active project-wide interval covers this calendar month (1–12). */
function monthIsLocked(locks: MonthLockSummary[], month: number): boolean {
  return locks.some((lock) => lock.fromMonth <= month && month <= lock.toMonth);
}

/** Solid muted (not /40–/50) so locked columns read as locked, not faded. */
const LOCKED_COLUMN_CLASS = "bg-muted text-muted-foreground";

/** Locked columns mute the whole group (header + cells); lock wins over current-month yellow. */
function monthColumnClass(locked: boolean, isCurrent: boolean, active: boolean): string {
  if (locked) return LOCKED_COLUMN_CLASS;
  if (isCurrent) return "bg-yellow/40 text-foreground";
  if (active) return "bg-accent/40 text-accent-foreground";
  return "";
}

/** Dashed rule before months 2–12. Year-total `border-l` stays solid. */
function monthDividerClass(month: number): string {
  return month > 1 ? "border-l border-dashed border-border" : "";
}

/** Extra left pad on H for months 2–12 (FuE → next H gap ~8px → ~16px). */
function monthHoursPadClass(month: number): string {
  return month > 1 ? "pl-2 pr-1" : "px-1";
}

/** Extra right pad on FuE for months 1–11 (same gap as `monthHoursPadClass`). */
function monthRdPadClass(month: number): string {
  return month < 12 ? "pl-1 pr-2" : "px-1";
}

/** Header group pad: same FuE→next-H edge extras as the inner H / FuE cells. */
function monthGroupPadClass(month: number): string {
  if (month === 1) return "pl-1 pr-2";
  if (month === 12) return "pl-2 pr-1";
  return "px-2";
}

/** Total-hours cell: dash when empty / out-of-range; otherwise rounded hours. */
function formatTotalOrDash(hours: number, showDash: boolean): string {
  if (showDash) return "—";
  return String(round2(hours));
}

/** R&D cell: dash when out-of-range, empty month, or zero R&D (screenshot parity). */
function formatRdOrDash(hours: number, showDash: boolean): string {
  if (showDash || hours === 0) return "—";
  return String(round2(hours));
}

export function ProjectOverviewStep({
  overview,
  year,
  years,
  onYearChange,
  monthsShort,
  onExportMatrix,
  onExportAttachmentsZip,
  onExportStundenzettelZip,
  onEdit,
}: {
  overview: LiveOverview;
  year: number;
  years: number[];
  onYearChange: (year: number) => void;
  monthsShort: string[];
  onExportMatrix: () => void;
  onExportAttachmentsZip?: () => void;
  onExportStundenzettelZip?: () => void;
  onEdit: (employee: LiveEmployeeMonth) => void;
}) {
  const { t } = useTranslation();
  const [activeMonth, setActiveMonth] = useState<number | null>(null);
  const hasEmployees = overview.employees.length > 0;

  const today = berlinToday();
  const berlinYear = Number(today.slice(0, 4));
  const berlinMonth = Number(today.slice(5, 7));
  const currentMonthInView = year === berlinYear ? berlinMonth : null;

  const holidayHint = overview.bundesland
    ? ` · ${t("projectLive.overviewHolidayHint", { bundesland: overview.bundesland })}`
    : "";

  return (
    <section className="mt-8" data-testid="live-overview-step">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2" data-testid="overview-year-filter">
          <Label className="text-sm font-medium">{t("projectLive.year")}</Label>
          <YearSelect
            value={year}
            onChange={onYearChange}
            years={years}
            ariaLabel={t("projectLive.year")}
            showTrackingMode
            className="w-44"
          />
        </div>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            data-testid="export-attachments-zip"
            disabled={!hasEmployees || overview.attachmentCount === 0}
            onClick={() => onExportAttachmentsZip?.()}
          >
            <Download className="size-4" /> {t("projectLive.exportAttachmentsZip")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-testid="export-stundenzettel-zip"
            disabled={!hasEmployees}
            onClick={() => onExportStundenzettelZip?.()}
          >
            <Download className="size-4" /> {t("projectLive.exportStundenzettelZip")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-testid="export-year-matrix"
            disabled={!hasEmployees}
            onClick={onExportMatrix}
          >
            <Download className="size-4" /> {t("projectLive.exportYearMatrix")}
          </Button>
        </div>
      </div>

      <div className="mt-4">
        <h2 className="text-lg">{t("projectLive.overviewTitle", { year })}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("projectLive.overviewSubtitle")}
          {holidayHint}
        </p>
      </div>

      <div
        className="mt-4 overflow-x-auto rounded-2xl border border-border bg-card"
        data-testid="overview-table-scroll"
      >
        <table className="w-full min-w-[1600px] border-separate border-spacing-0 text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <th
                rowSpan={2}
                data-testid="overview-sticky-employee-header"
                className={STICKY_EMPLOYEE_HEADER}
              >
                {t("projectLive.employee")}
              </th>
              {monthsShort.map((label, index) => {
                const month = index + 1;
                const active = activeMonth === month;
                const isCurrent = currentMonthInView === month;
                const locked = monthIsLocked(overview.locks, month);
                return (
                  <th
                    key={month}
                    colSpan={2}
                    data-testid={`overview-month-header-${month}`}
                    data-locked={locked ? "true" : undefined}
                    className={`py-2 text-center font-medium ${monthGroupPadClass(month)} ${monthDividerClass(month)} ${monthColumnClass(locked, isCurrent, active)}${
                      active && isCurrent && !locked ? " ring-2 ring-inset ring-accent" : ""
                    }`}
                  >
                    <button
                      type="button"
                      className={`inline-flex w-full items-center justify-center gap-1 rounded px-1 py-1 ${
                        active
                          ? "bg-accent font-semibold text-accent-foreground"
                          : "hover:bg-muted/60"
                      }`}
                      title={locked ? t("monthLock.lockedHint") : undefined}
                      onClick={() => setActiveMonth(active ? null : month)}
                    >
                      {locked ? (
                        <Lock
                          className="size-3 shrink-0"
                          aria-hidden
                          data-testid={`overview-month-lock-${month}`}
                        />
                      ) : null}
                      {label}
                    </button>
                  </th>
                );
              })}
              <th
                colSpan={3}
                data-testid="overview-year-total-divider"
                className="border-l border-border px-3 py-2 text-center font-medium"
              >
                {t("projectLive.yearTotalGroup")}
              </th>
              <th
                rowSpan={2}
                data-testid="overview-sticky-action-header"
                className={STICKY_ACTION_HEADER}
              >
                {t("projectLive.action")}
              </th>
            </tr>
            <tr className="border-b border-border bg-muted/40 text-xs uppercase tracking-widest text-muted-foreground">
              {monthsShort.map((_, index) => {
                const month = index + 1;
                const active = activeMonth === month;
                const isCurrent = currentMonthInView === month;
                const locked = monthIsLocked(overview.locks, month);
                const tone = locked
                  ? LOCKED_COLUMN_CLASS
                  : isCurrent
                    ? "bg-yellow/40 text-foreground"
                    : active
                      ? "bg-accent/20"
                      : "";
                return (
                  <th
                    key={`sub-${month}`}
                    colSpan={2}
                    className={`p-0 font-medium ${monthDividerClass(month)} ${tone}`}
                  >
                    <div className="grid grid-cols-2">
                      <span className={`py-1.5 text-center ${monthHoursPadClass(month)}`}>
                        {t("projectLive.colHours")}
                      </span>
                      <span className={`py-1.5 text-center ${monthRdPadClass(month)}`}>
                        {t("projectLive.colRd")}
                      </span>
                    </div>
                  </th>
                );
              })}
              <th className="border-l border-border px-2 py-1.5 text-center font-medium">
                {t("projectLive.colHours")}
              </th>
              <th className="px-2 py-1.5 text-center font-medium">{t("projectLive.colRd")}</th>
              <th className="px-2 py-1.5 text-center font-medium">{t("projectLive.colPct")}</th>
            </tr>
          </thead>
          <tbody>
            {!hasEmployees ? (
              <tr>
                <td colSpan={OVERVIEW_COL_COUNT} className="p-8 text-center text-muted-foreground">
                  {t("projectLive.emptyEmployees")}
                </td>
              </tr>
            ) : (
              overview.employees.map((employee) => {
                const yearTotal = employee.months.reduce((sum, m) => sum + m.totalHours, 0);
                const yearRd = employee.months.reduce((sum, m) => sum + m.rdHours, 0);
                const yearEmpty = yearTotal <= 0 && yearRd <= 0;
                const pct = rdSharePct(yearRd, yearTotal);

                return (
                  <tr key={employee.userId} className="border-b border-border/60">
                    <td data-testid="overview-sticky-employee" className={STICKY_EMPLOYEE_CELL}>
                      <div className="font-medium">{formatPersonName(employee)}</div>
                      <div className="text-xs text-muted-foreground">{employee.email}</div>
                    </td>
                    {employee.months.map((cell) => {
                      const outOfRange = !cell.inTrackingWindow;
                      const empty = !outOfRange && cell.totalHours === 0 && cell.rdHours === 0;
                      const active = activeMonth === cell.month;
                      const isCurrent = currentMonthInView === cell.month;
                      const locked = monthIsLocked(overview.locks, cell.month);
                      const highlight = [
                        locked ? LOCKED_COLUMN_CLASS : "",
                        !locked && isCurrent ? "bg-yellow/30" : "",
                        !locked && active && !isCurrent ? "bg-accent/20" : "",
                        !locked && active && isCurrent
                          ? "bg-yellow/40 ring-1 ring-inset ring-accent/50"
                          : "",
                        outOfRange && !locked ? "bg-muted/40 text-muted-foreground/40" : "",
                      ]
                        .filter(Boolean)
                        .join(" ");
                      const showDash = outOfRange || empty;
                      const title = outOfRange ? t("projectLive.outsideEmployment") : undefined;

                      return (
                        <td
                          key={cell.month}
                          colSpan={2}
                          className={`p-0 tabular-nums ${monthDividerClass(cell.month)} ${highlight}`}
                          title={title}
                        >
                          <div className="grid grid-cols-2">
                            <span
                              data-testid={`overview-month-h-${employee.userId}-${cell.month}`}
                              className={`py-3 text-center ${monthHoursPadClass(cell.month)}`}
                            >
                              {formatTotalOrDash(cell.totalHours, showDash)}
                            </span>
                            <span
                              data-testid={`overview-month-rd-${employee.userId}-${cell.month}`}
                              className={`py-3 text-center ${monthRdPadClass(cell.month)}`}
                            >
                              {formatRdOrDash(cell.rdHours, showDash)}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                    <td
                      className="border-l border-border px-2 py-3 text-center tabular-nums"
                      data-testid={`overview-year-total-${employee.userId}`}
                    >
                      {formatTotalOrDash(yearTotal, yearEmpty)}
                    </td>
                    <td
                      className="px-2 py-3 text-center tabular-nums"
                      data-testid={`overview-year-rd-${employee.userId}`}
                    >
                      {formatRdOrDash(yearRd, yearEmpty)}
                    </td>
                    <td
                      className="px-2 py-3 text-center tabular-nums"
                      data-testid={`overview-year-pct-${employee.userId}`}
                    >
                      {pct === null ? "—" : `${pct}%`}
                    </td>
                    <td data-testid="overview-sticky-action" className={STICKY_ACTION_CELL}>
                      <Button variant="outline" size="sm" onClick={() => onEdit(employee)}>
                        <Pencil className="size-3.5" /> {t("adminEdit.open")}
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-2">
          <span
            className="inline-block size-3 rounded-sm bg-yellow/60 ring-1 ring-border"
            aria-hidden
          />
          {t("projectLive.currentMonthLegend")}
        </span>
        <span>{t("projectLive.scrollHint")}</span>
      </div>
    </section>
  );
}
